/**
 * Module dependencies.
 */

import React from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { cn } from 'app/lib/utils';

/**
 * Brand-aligned series colors (CSS design tokens where possible).
 */

export const chartColors = {
  primary: 'hsl(var(--primary))',
  destructive: 'hsl(var(--destructive))',
  muted: 'hsl(var(--muted-foreground))'
};

/**
 * Donut/category palette — restrained, finance-friendly hues.
 */

export const chartPalette = [
  '#1B752B',
  '#2f5c9f',
  '#c1c167',
  '#67bf65',
  '#b57edc',
  '#3fafaf',
  '#cc944a',
  '#9f2d2f',
  '#90a8d4',
  '#5e5e5e'
];

/**
 * `Series` type.
 */

export type Series = {
  key: string;
  label: string;
  color: string;
};

/**
 * Shared axis/grid styling.
 */

const axisProps = {
  tickLine: false,
  axisLine: false,
  style: { fontSize: 11, fill: 'hsl(var(--muted-foreground))' }
} as const;

/**
 * Compact number formatter for numeric axes (e.g. 1.2k, 3.4M).
 */

function compactNumber(value: number): string {
  const abs = Math.abs(value);

  if (abs >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  }

  if (abs >= 1_000) {
    return `${(value / 1_000).toFixed(1).replace(/\.0$/, '')}k`;
  }

  return String(value);
}

/**
 * `ChartTooltip` component — themed to match the app popovers.
 */

function ChartTooltip({ active, payload, label, valueFormatter }: any): JSX.Element | null {
  if (!active || !payload?.length) {
    return null;
  }

  // Prefer a richer per-datum label (e.g. a full date incl. the year) when the
  // data provides one; fall back to the axis label otherwise.
  const displayLabel = payload[0]?.payload?.fullLabel ?? label;

  return (
    <div className={'rounded-lg border border-border bg-popover px-3 py-2 text-xs shadow-lg'}>
      {displayLabel !== undefined && <div className={'mb-1 font-medium text-foreground'}>{displayLabel}</div>}

      <div className={'space-y-0.5'}>
        {payload.map((entry: any) => (
          <div className={'flex items-center justify-between gap-4'} key={entry.dataKey ?? entry.name}>
            <span className={'flex items-center gap-1.5 text-muted-foreground'}>
              <span
                className={'inline-block h-2 w-2 rounded-full'}
                style={{ backgroundColor: entry.color ?? entry.payload?.fill }}
              />

              {entry.name}
            </span>

            <span className={'font-medium tabular-nums text-foreground'}>
              {valueFormatter ? valueFormatter(entry.value) : entry.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * `Legend` renderer — compact, muted.
 */

function renderLegend(props: any): JSX.Element {
  const { payload } = props;

  return (
    <div className={'flex flex-wrap items-center justify-center gap-4 pb-1 text-xs text-muted-foreground'}>
      {payload?.map((entry: any) => (
        <span className={'flex items-center gap-1.5'} key={entry.value}>
          <span className={'inline-block h-2 w-2 rounded-full'} style={{ backgroundColor: entry.color }} />

          {entry.value}
        </span>
      ))}
    </div>
  );
}

/**
 * Export `AreaTrendChart` component.
 */

export function AreaTrendChart({
  data,
  series,
  xKey,
  height = 300,
  valueFormatter,
  onPointClick,
  highlightX,
  yDomain
}: {
  data: any[];
  series: Series[];
  xKey: string;
  height?: number;
  valueFormatter?: (value: number) => string;
  onPointClick?: (index: number) => void;
  highlightX?: string | number;
  yDomain?: [number, number];
}): JSX.Element {
  return (
    <ResponsiveContainer
      className={'[&_.recharts-surface]:outline-none [&_.recharts-wrapper]:outline-none'}
      height={height}
      width={'100%'}
    >
      <AreaChart
        data={data}
        margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
        onClick={(state: any) => {
          if (onPointClick && state && typeof state.activeTooltipIndex === 'number') {
            onPointClick(state.activeTooltipIndex);
          }
        }}
        style={onPointClick ? { cursor: 'pointer' } : undefined}
      >
        <defs>
          {series.map(item => (
            <linearGradient id={`fill-${item.key}`} key={item.key} x1={'0'} x2={'0'} y1={'0'} y2={'1'}>
              <stop offset={'5%'} stopColor={item.color} stopOpacity={0.2} />

              <stop offset={'95%'} stopColor={item.color} stopOpacity={0.02} />
            </linearGradient>
          ))}
        </defs>

        <CartesianGrid stroke={'hsl(var(--border))'} strokeDasharray={'3 3'} vertical={false} />

        {highlightX != null && (
          <ReferenceLine
            ifOverflow={'extendDomain'}
            stroke={'hsl(var(--primary))'}
            strokeDasharray={'4 3'}
            strokeWidth={1.5}
            x={highlightX}
          />
        )}

        <XAxis dataKey={xKey} {...axisProps} />

        <YAxis {...axisProps} domain={yDomain} tickFormatter={compactNumber} width={44} />

        <Tooltip content={<ChartTooltip valueFormatter={valueFormatter} />} cursor={{ stroke: 'hsl(var(--border))' }} />

        <Legend content={renderLegend} />

        {series.map(item => (
          <Area
            dataKey={item.key}
            fill={`url(#fill-${item.key})`}
            key={item.key}
            name={item.label}
            stroke={item.color}
            strokeWidth={2}
            type={'monotone'}
          />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  );
}

/**
 * Export `DonutChart` component.
 */

export function DonutChart({
  data,
  height = 260,
  valueFormatter
}: {
  data: { name: string; value: number }[];
  height?: number;
  valueFormatter?: (value: number) => string;
}): JSX.Element {
  return (
    <ResponsiveContainer height={height} width={'100%'}>
      <PieChart>
        <Pie
          cx={'50%'}
          cy={'50%'}
          data={data}
          dataKey={'value'}
          innerRadius={'62%'}
          nameKey={'name'}
          outerRadius={'90%'}
          paddingAngle={2}
          stroke={'hsl(var(--card))'}
          strokeWidth={2}
        >
          {data.map((entry, index) => (
            <Cell fill={chartPalette[index % chartPalette.length]} key={entry.name} />
          ))}
        </Pie>

        <Tooltip content={<ChartTooltip valueFormatter={valueFormatter} />} />
      </PieChart>
    </ResponsiveContainer>
  );
}

/**
 * Export `CategoryBarChart` component.
 */

export function CategoryBarChart({
  data,
  series,
  xKey,
  height = 320,
  layout = 'horizontal',
  valueFormatter,
  className,
  onBarClick
}: {
  data: any[];
  series: Series[];
  xKey: string;
  height?: number;
  layout?: 'horizontal' | 'vertical';
  valueFormatter?: (value: number) => string;
  className?: string;
  onBarClick?: (row: any) => void;
}): JSX.Element {
  const vertical = layout === 'vertical';

  return (
    <div className={cn('w-full', className)}>
      <ResponsiveContainer height={height} width={'100%'}>
        <BarChart data={data} layout={layout} margin={{ top: 8, right: 8, bottom: 0, left: vertical ? 8 : 0 }}>
          <CartesianGrid
            horizontal={!vertical}
            stroke={'hsl(var(--border))'}
            strokeDasharray={'3 3'}
            vertical={vertical}
          />

          {vertical ? (
            <>
              <XAxis type={'number'} {...axisProps} tickFormatter={compactNumber} />

              <YAxis dataKey={xKey} type={'category'} width={120} {...axisProps} />
            </>
          ) : (
            <>
              <XAxis dataKey={xKey} {...axisProps} />

              <YAxis type={'number'} width={44} {...axisProps} tickFormatter={compactNumber} />
            </>
          )}

          <Tooltip content={<ChartTooltip valueFormatter={valueFormatter} />} cursor={{ fill: 'hsl(var(--muted))' }} />

          <Legend content={renderLegend} />

          {series.map(item => (
            <Bar
              cursor={onBarClick ? 'pointer' : undefined}
              dataKey={item.key}
              fill={item.color}
              key={item.key}
              name={item.label}
              onClick={(row: any) => onBarClick?.(row)}
              radius={vertical ? [0, 4, 4, 0] : [4, 4, 0, 0]}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
